import AddPersonForm, { type CreatedPerson } from '@/components/features/People/forms/AddPersonForm';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { useControllableOpen } from '@/hooks/use-controllable-open';
import { useTranslations } from '@/hooks/useTranslations';
import { useEffect, useState, type ReactNode } from 'react';
import { toast } from 'sonner';

interface CreatePersonModalProps {
    /** Omit when the host drives the modal with `open`/`onOpenChange`. */
    trigger?: ReactNode;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    /** When set, the company is preselected and locked in the form. */
    companyId?: string | null;
    companyName?: string | null;
    onCreated?: (person: CreatedPerson) => void;
}

/**
 * Creates a contact without leaving the current page.
 *
 * The contact is saved immediately over the JSON API, so it survives even if the
 * form that launched this modal (e.g. a half-filled lead) is never submitted.
 */
export default function CreatePersonModal({
    trigger,
    open: openProp,
    onOpenChange,
    companyId = null,
    companyName = null,
    onCreated,
}: CreatePersonModalProps) {
    const { t } = useTranslations();
    const [isOpen, setIsOpen] = useControllableOpen(openProp, onOpenChange);
    // Remounts the form on each open so a cancelled draft is not carried over.
    const [formKey, setFormKey] = useState(0);

    const handleOpenChange = (open: boolean) => {
        setIsOpen(open);
    };

    // Covers both ways in: the trigger above and a host that flips `open`
    // itself, which never runs onOpenChange.
    useEffect(() => {
        if (isOpen) {
            setFormKey((previous) => previous + 1);
        }
    }, [isOpen]);

    return (
        <ResponsiveModal open={isOpen} onOpenChange={handleOpenChange} variant="sheet">
            {trigger ? <ResponsiveModalTrigger asChild>{trigger}</ResponsiveModalTrigger> : null}

            <ResponsiveModalContent size="xl">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('people.forms.add.dialog.title', 'New contact')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t('people.forms.add.dialog.description', 'The contact is saved immediately and stays even if you do not finish the lead.')}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                {/* The form supplies its own cancel/create buttons, so there is no
                    ResponsiveModalFooter here. */}
                <ResponsiveModalBody>
                    <AddPersonForm
                        key={formKey}
                        variant="modal"
                        companyId={companyId}
                        companyName={companyName}
                        onCreated={(person) => {
                            onCreated?.(person);
                            toast.success(t('people.forms.add.dialog.created', 'Contact created'));
                            setIsOpen(false);
                        }}
                        onCancel={() => setIsOpen(false)}
                    />
                </ResponsiveModalBody>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
